import React, { Suspense, ComponentType, LazyExoticComponent } from "react"; // Added ComponentType, LazyExoticComponent

import type { RouterStore } from "stores/RouterStore";
import injectStores from "components/RootStore/injectStores";
import Card from "components/Card/Card";
import CardHeader from "components/Card/CardHeader";
import CardBody from "components/Card/CardBody";
import { ErrorBoundary } from "components/ErrorBoundary";
import SkeletonCard from "components/Skeleton/SkeletonCard";
import SkeletonCardHeader from "components/Skeleton/SkeletonCard.Headery";
import SkeletonCardBody from "components/Skeleton/SkeletonCard.Body";
import componentFromLazy from "components/Lazy/ComponentFromLazy";
import Spacer from "components/Spacer/Spacer";
import { computed } from "mobx";
import DataGrid from "components/DataGrid/DataGrid";
import { ScheduleGrid } from "components/ScheduleGrid";
import ViewStore from "stores/ViewStore";
import { ScheduleGridProps } from "types/ScheduleGridTypes";
import { DataGridProps } from "types/DataGridTypes";

// Helper function to check if a component is a React.lazy component
function isLazyExoticComponent(
  component: unknown
): component is LazyExoticComponent<ComponentType<unknown>> {
  return (
    component != null &&
    (component as { $$typeof?: symbol }).$$typeof === Symbol.for("react.lazy")
  );
}

interface ViewportCardProviderProps {
  routerStore?: RouterStore;
  viewStore?: ViewStore;
}

const DataGridBodyProvider = (props: DataGridProps) => (
  <ErrorBoundary name={props.namespace || "DataGridBodyProvider"}>
    <div style={{ overflow: "hidden", height: "100%", width: "100%" }}>
      <DataGrid {...props} />
    </div>
  </ErrorBoundary>
);

const ScheduleGridProvider = (props: ScheduleGridProps) => (
  <ErrorBoundary name={props.namespace || "ScheduleGridProvider"}>
    <ScheduleGrid {...props} />
  </ErrorBoundary>
);

const ViewportCardProvider: React.FC = injectStores(
  (props: ViewportCardProviderProps) => {
    // trace();
    const { routerStore } = props;
    const renderSkeleton = computed(
      () =>
        !routerStore ||
        !routerStore.currentRoute ||
        !routerStore.currentRoute.viewportCard
    ).get();
    if (renderSkeleton) return <SkeletonCard />;
    const currentViewportCard = computed(
      () => routerStore?.currentRoute?.viewportCard ?? null
    ).get();
    // console.log('currentViewportCard', currentViewportCard);

    let CardBodyComponent: ComponentType<unknown> | null = SkeletonCardBody; // Ensure type compatibility
    let lazyLoadComponent = false;

    const headerComponentInput = currentViewportCard?.header?.component;
    const CardHeaderComponent = headerComponentInput
      ? isLazyExoticComponent(headerComponentInput)
        ? (headerComponentInput as LazyExoticComponent<ComponentType<unknown>>) // Use directly
        : componentFromLazy(
            headerComponentInput as () => Promise<{
              default: ComponentType<unknown>;
            }>
          ) // Process with componentFromLazy
      : null;

    if (currentViewportCard?.body?.gridContainer) {
      CardBodyComponent = () => (
        <DataGridBodyProvider
          // width={viewStore?.cardBodySize?.width}
          // height={viewStore?.cardBodySize?.height}
          {...currentViewportCard?.body?.gridContainer}
          columnHeaders={currentViewportCard?.body?.gridContainer?.columnHeaders || []}
          storeName={currentViewportCard?.body?.gridContainer?.storeName || ''}
        />
      );
    } else if (currentViewportCard?.body?.scheduleContainer) {
      // const {
      //   storeName,
      //   columnHeaders,
      //   dateKey,
      //   dateRangeKey,
      //   rowHeight,
      //   columnWidth,
      //   namespace,
      // } = currentViewportCard.body.scheduleContainer;

      // console.log(
      //   'scheduleContainer body',
      //   currentViewportCard.body.scheduleContainer
      // );
      CardBodyComponent = () => (
        <ScheduleGridProvider
          storeName={
            currentViewportCard?.body?.scheduleContainer?.storeName as string
          }
          // columnHeaders={columnHeaders}
          // dateKey={dateKey}
          // dateRangeKey={dateRangeKey}
          // rowHeight={rowHeight}
          // columnWidth={columnWidth}
          // namespace={namespace}
          {...currentViewportCard?.body?.scheduleContainer}
        />
      );
    } else if (currentViewportCard?.body?.component) {
      lazyLoadComponent = true;
      const bodyComponentInput = currentViewportCard.body.component;
      CardBodyComponent = isLazyExoticComponent(bodyComponentInput)
        ? (bodyComponentInput as LazyExoticComponent<ComponentType<unknown>>)
        : componentFromLazy(
            bodyComponentInput as () => Promise<{
              default: ComponentType<unknown>;
            }>
          );
    }

    return (
      <ErrorBoundary name="ViewportCardProvider">
        <Suspense fallback={<SkeletonCard />}>
          <Card viewportCard={true}>
            <CardHeader
              viewportCard={true}
              enableBackButton={
                currentViewportCard?.header?.enableBackButton || false
              }
              enableToolbox={
                currentViewportCard?.header?.enableToolbox || false
              }
              enableRefreshButton={
                currentViewportCard?.header?.enableRefreshButton || false
              }
              color={currentViewportCard?.header?.color || "primary"}
              title={currentViewportCard?.header?.title || "Loading..."}
            >
              <Suspense fallback={<SkeletonCardHeader />}>
                {CardHeaderComponent ? <CardHeaderComponent /> : <Spacer />}
              </Suspense>
            </CardHeader>
            <CardBody
              grayBackground={
                currentViewportCard?.body?.grayBackground ? true : false
              }
            >
              {lazyLoadComponent ? (
                <Suspense fallback={<SkeletonCardBody />}>
                  {CardBodyComponent ? <CardBodyComponent /> : null}
                </Suspense>
              ) : CardBodyComponent ? (
                <CardBodyComponent />
              ) : null}
            </CardBody>
          </Card>
        </Suspense>
      </ErrorBoundary>
    );
  }
);

export default ViewportCardProvider;
